/**
 * One field on the review screen: what the scanner saw, and a box to fix it in.
 *
 * The crop image is the point of the whole screen — the receptionist sees
 * exactly what the scanner saw and corrects it in two seconds, *before* the
 * guest is handed a document with their surname spelled wrong. It is not in the
 * card any more, though: in the card it made a flagged field half as tall again
 * as the plain field beside it, and a deck of thirty cards where six are taller
 * than the rest reads as a broken grid. It hangs off the state chip instead —
 * point at "check", or tab to it, and the crop and the characters the scanner
 * made of it appear over the card. Every card is the same height whether the
 * scanner was sure of it or not.
 *
 * What a field's state is gets one word rather than a number. "0.92" next to
 * every row told a receptionist nothing they could act on, and next to a field
 * nothing had ever read it was worse than nothing: a full green bar asserting
 * certainty about an empty box. The four states that matter are whether the
 * scanner read it well, read it badly, never read it, or a person has since
 * fixed it. The number is on hover for anyone who wants it.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Pencil, ScanLine } from 'lucide-react'
import type { Extraction } from '@/lib/types'
import { FieldControl, pickerFallbackNote } from '@/components/domain/FieldControl'
import { cn } from '@/lib/utils'

export function FieldRow({
  extraction,
  value,
  active,
  failed,
  onFocus,
  onBlur,
  onChange,
}: {
  extraction: Extraction
  value: string
  active: boolean
  failed: boolean
  onFocus: () => void
  onBlur: () => void
  onChange: (value: string) => void
}) {
  const flagged = extraction.needs_review
  const missing = extraction.is_required && !value.trim()
  const showRaw = Boolean(extraction.raw_text) && extraction.raw_text !== value
  const note = pickerFallbackNote(extraction, value)
  const noteId = `field-${extraction.field_key}-note`
  const chip = chipFor(extraction, failed, missing)

  // Only a field the scanner was unsure of has anything worth showing: a crop
  // of a field it read confidently is the value already in the box.
  const scan = flagged && (extraction.crop_url || showRaw)

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-[10px] border p-2.5 transition-colors',
        // "Needs checking" and "has focus" must not be told apart by hue
        // alone. Now that the brand is orange it sits between the amber
        // warning and the crimson danger, so the flag carries a thick
        // leading edge as well as its fill, and focus is a ring. Different
        // shapes, not just different colours.
        //
        // The edge is painted over the border rather than being a thicker
        // one: a 3px border ate 2px of the content box, so a flagged card's
        // box sat 2px in from every unflagged box beside it and the column
        // of boxes had a jog in it wherever the scanner was unsure.
        'before:absolute before:inset-y-0 before:start-0 before:w-[3px] before:rounded-s-[9px]',
        failed
          ? 'border-[var(--danger)] bg-[var(--danger-subtle)] before:bg-[var(--danger)]'
          : flagged || missing
            ? 'border-[var(--warning)] bg-[var(--warning-subtle)] before:bg-[var(--warning)]'
            : 'border-[var(--border)]',
        active && 'ring-2 ring-[var(--brand)]',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={`field-${extraction.field_key}`}
          className="truncate text-[12px] font-medium text-[var(--text)]"
        >
          {extraction.label}
          {extraction.is_required && (
            <span className="ms-0.5 text-[var(--danger)]" aria-label="required">
              *
            </span>
          )}
        </label>
        {scan ? (
          <ScanPeek extraction={extraction} chip={chip} showRaw={showRaw} />
        ) : (
          <Chip {...chip} />
        )}
      </div>

      {/* The box is the same width and in the same place in every card, flagged
          or not: a deck of them reads as one column of boxes rather than a
          ragged edge. */}
      <div className="mt-1.5">
        <FieldControl
          id={`field-${extraction.field_key}`}
          spec={extraction}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          aria-describedby={note || extraction.help_text ? noteId : undefined}
        />
      </div>

      {/* The two things that still take a line of their own. Both say why this
          box is not behaving normally, which is not something to go looking for
          under a hover. Both are rare - a picker that refused the scanned value,
          and a save that did not land. */}
      {(note || extraction.help_text) && (
        <p
          id={noteId}
          className={cn(
            'mt-1 text-[11px]',
            note ? 'text-[var(--warning)]' : 'text-[var(--text-muted)]',
          )}
        >
          {note ?? extraction.help_text}
        </p>
      )}

      {failed && (
        <p role="alert" className="mt-1 text-[11px] text-[var(--danger)]">
          Not saved. Change it again to retry.
        </p>
      )}
    </div>
  )
}

/**
 * The state chip, with the scan hanging off it.
 *
 * It opens on hover for the mouse, on focus for the keyboard, and stays open on
 * a click for a touch screen and for anyone who wants to type with it open. A
 * card near the foot of the deck opens it upwards, because the deck scrolls and
 * a panel that hangs below such a card is cut off by the scroller.
 */
function ScanPeek({
  extraction,
  chip,
  showRaw,
}: {
  extraction: Extraction
  chip: ChipSpec
  showRaw: boolean
}) {
  // Three ways in, because three kinds of person: the mouse points at it, the
  // keyboard tabs to it, and a click pins it - a touch screen has no hover, and
  // a receptionist comparing the crop against what they are typing wants both
  // hands free.
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [up, setUp] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  const panelId = `field-${extraction.field_key}-scan`
  const open = hovered || focused || pinned

  // A pinned panel closes the way any pinned thing does: click away from it.
  useEffect(() => {
    if (!pinned) return
    const close = (event: MouseEvent) => {
      if (!anchor.current?.contains(event.target as Node)) setPinned(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [pinned])

  useLayoutEffect(() => {
    if (!open || !anchor.current) return
    // Whichever runs out first - the deck's scroller, or the window. The deck
    // only scrolls when it is tall enough to need to, so the window is the
    // limit that usually applies.
    const scroller = anchor.current.closest('.overflow-y-auto')
    const limit = Math.min(
      window.innerHeight,
      scroller?.getBoundingClientRect().bottom ?? Infinity,
    )
    setUp(limit - anchor.current.getBoundingClientRect().bottom < 120)
  }, [open])

  return (
    <div
      ref={anchor}
      // `flex`, not a block: a block wraps the inline-flex chip in a line box
      // and the strut under the baseline made the header - and so the whole
      // card - two pixels taller than every card without a chip to open.
      className="relative flex shrink-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={() => setPinned((was) => !was)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          setPinned(false)
          setHovered(false)
        }}
        className={cn(
          chipClass(chip.tone),
          'cursor-help underline-offset-2 hover:underline',
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--brand)]',
        )}
      >
        {chip.icon}
        {chip.text}
        <span className="sr-only">
          {' '}
          — show what the scanner read for {extraction.label}
        </span>
      </button>

      {open && (
        // The gap between chip and panel is padding on a wrapper rather than a
        // margin, so it belongs to the panel. As a margin it was no man's land:
        // the pointer crossing it was over neither element, `mouseleave` fired,
        // and the panel closed in the face of anyone moving towards it.
        <div
          className={cn('absolute end-0 z-20 w-56', up ? 'bottom-full pb-1' : 'top-full pt-1')}
        >
          <div
            id={panelId}
            role="tooltip"
            className="rounded-[10px] border border-[var(--border)] bg-[var(--surface-2)] p-2 shadow-lg"
          >
            {extraction.crop_url && (
              <img
                src={extraction.crop_url}
                alt={`Scanned image of ${extraction.label}`}
                className="h-10 w-full rounded border border-[var(--border)] bg-white object-contain"
              />
            )}
            {showRaw && (
              <p className="mt-1.5 break-words font-mono text-[11px] text-[var(--text-muted)]">
                read as “{extraction.raw_text}”
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * One word for where this field stands.
 *
 * Ordered by what a person needs to know first: a failed save beats a missing
 * required value, which beats a doubtful reading, which beats a correction
 * someone has already made.
 */
function chipFor(extraction: Extraction, failed: boolean, missing: boolean): ChipSpec {
  if (failed) return { tone: 'danger', icon: <AlertTriangle />, text: 'not saved' }
  if (missing) return { tone: 'warning', icon: <AlertTriangle />, text: 'needed' }
  if (extraction.needs_review) return { tone: 'warning', icon: <ScanLine />, text: 'check' }
  if (extraction.corrected) return { tone: 'brand', icon: <Pencil />, text: 'edited' }

  // A field with no reading gets no verdict on its reading. It is still on the
  // document and still fillable — it simply was not scanned.
  if (!extraction.was_read || extraction.confidence == null) {
    return { tone: 'muted', text: 'not read' }
  }
  return {
    tone: 'success',
    icon: <CheckCircle2 />,
    text: 'read',
    title: `The scanner was ${Math.round(extraction.confidence * 100)}% sure of this`,
  }
}

interface ChipSpec {
  tone: 'danger' | 'warning' | 'success' | 'brand' | 'muted'
  icon?: React.ReactNode
  text: string
  title?: string
}

/**
 * Every chip is the same box, whether it is a button or not: at text-[11px]
 * with no padding the "not read" one was a pixel shorter than the rest, and a
 * pixel is enough to stop a line of cards ending on one edge.
 */
function chipClass(tone: ChipSpec['tone']) {
  return cn(
    'inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
    '[&>svg]:h-3 [&>svg]:w-3',
    tone === 'danger' && 'bg-[var(--danger-subtle)] text-[var(--danger)]',
    tone === 'warning' && 'bg-[var(--warning-subtle)] text-[var(--warning)]',
    tone === 'success' && 'text-[var(--text-muted)]',
    tone === 'brand' && 'text-[var(--brand)]',
    tone === 'muted' && 'text-[var(--text-muted)]',
  )
}

function Chip({ tone, icon, text, title }: ChipSpec) {
  return (
    <span title={title} className={chipClass(tone)}>
      {icon}
      {text}
    </span>
  )
}
